<template>
  <div
    v-if="isVisible"
    class="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
    @click="closeModal"
  >
    <div
      class="card-neumorphic max-w-md w-full max-h-[90vh] overflow-y-auto"
      @click.stop
      v-motion
      :initial="{ opacity: 0, scale: 0.8 }"
      :enter="{ opacity: 1, scale: 1 }"
      :leave="{ opacity: 0, scale: 0.8 }"
      :duration="300"
    >
      <!-- Header -->
      <div class="flex justify-between items-center mb-6">
        <h3 class="text-2xl font-poppins font-semibold text-dark-gray">
          {{ modalTitle }}
        </h3>
        <button
          @click="closeModal"
          class="w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors duration-200"
        >
          <X :size="20" class="text-dark-gray" />
        </button>
      </div>

      <!-- Form -->
      <form @submit.prevent="handleSubmit" class="space-y-4">
        <div class="grid grid-cols-2 gap-4">
          <div>
            <label class="block text-dark-gray/70 font-medium mb-2">
              Full Name *
            </label>
            <input
              type="text"
              v-model="formData.name"
              class="input-neumorphic"
              placeholder="Enter your name"
              required
            />
          </div>
          <div>
            <label class="block text-dark-gray/70 font-medium mb-2">
              Phone *
            </label>
            <input
              type="tel"
              v-model="formData.phone"
              class="input-neumorphic"
              placeholder="Phone number"
              required
            />
          </div>
        </div>

        <div>
          <label class="block text-dark-gray/70 font-medium mb-2">
            Email *
          </label>
          <input
            type="email"
            v-model="formData.email"
            class="input-neumorphic"
            placeholder="your@email.com"
            required
          />
        </div>

        <div>
          <label class="block text-dark-gray/70 font-medium mb-2">
            Organization
          </label>
          <input
            type="text"
            v-model="formData.organization"
            class="input-neumorphic"
            placeholder="Company/Organization"
          />
        </div>

        <div>
          <label class="block text-dark-gray/70 font-medium mb-2">
            Preferred Date
          </label>
          <input
            type="date"
            v-model="formData.preferredDate"
            class="input-neumorphic"
          />
        </div>

        <div>
          <label class="block text-dark-gray/70 font-medium mb-2">
            Additional Requirements
          </label>
          <textarea
            v-model="formData.message"
            rows="3"
            class="input-neumorphic resize-none"
            placeholder="Any specific requirements or questions..."
          ></textarea>
        </div>

        <div class="flex gap-4 pt-4">
          <button
            type="button"
            @click="closeModal"
            class="flex-1 btn-neumorphic text-dark-gray hover:text-gray-600"
          >
            Cancel
          </button>
          <button
            type="submit"
            class="flex-1 btn-neumorphic text-light-purple hover:text-white hover:bg-light-purple relative overflow-hidden"
            :disabled="isSubmitting"
          >
            <span class="relative z-10">
              {{ isSubmitting ? 'Sending...' : 'Submit Request' }}
            </span>
            <div
              class="absolute inset-0 bg-light-purple transition-all duration-300 opacity-0 hover:opacity-100"
              style="border-radius: 24px"
            />
          </button>
        </div>
      </form>

      <!-- Success Message -->
      <div
        v-if="isSubmitted"
        class="absolute inset-0 bg-white/95 flex items-center justify-center rounded-neumorphic"
        v-motion
        :initial="{ opacity: 0 }"
        :enter="{ opacity: 1 }"
        :duration="300"
      >
        <div class="text-center">
          <CheckCircle :size="64" class="mx-auto mb-4 text-light-purple" />
          <h3 class="text-2xl font-semibold text-dark-gray mb-2">Request ready!</h3>
          <p class="text-dark-gray/70 px-4">
            WhatsApp should open with your details — tap Send to 75 9409 9409.
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, reactive, computed } from 'vue'
import { X, CheckCircle } from 'lucide-vue-next'
import { CONTACT_EMAIL, CONTACT_PHONE_DISPLAY } from '../config/contact'
import { submitForm } from '../services/submitForm'

export default {
  name: 'ServiceModal',
  components: {
    X,
    CheckCircle
  },
  props: {
    isVisible: {
      type: Boolean,
      default: false
    },
    serviceType: {
      type: String,
      default: ''
    }
  },
  emits: ['close'],
  setup(props, { emit }) {
    const isSubmitting = ref(false)
    const isSubmitted = ref(false)

    const formData = reactive({
      name: '',
      phone: '',
      email: '',
      organization: '',
      preferredDate: '',
      message: ''
    })

    const modalTitle = computed(() => {
      return props.serviceType === 'home-collection' 
        ? 'Request Home Collection' 
        : 'Book Corporate Camp'
    })

    const closeModal = () => {
      if (!isSubmitting.value) {
        emit('close')
        // Reset form after a delay
        setTimeout(() => {
          Object.keys(formData).forEach(key => {
            formData[key] = ''
          })
          isSubmitted.value = false
        }, 300)
      }
    }

    const handleSubmit = async () => {
      isSubmitting.value = true
      const serviceLabel =
        props.serviceType === 'home-collection'
          ? 'Home Blood Collection'
          : 'Corporate Medical Camp'

      try {
        await submitForm({
          subject: `Service request — ${serviceLabel}`,
          formName: 'Service Modal',
          data: {
            service: serviceLabel,
            name: formData.name,
            phone: formData.phone,
            email: formData.email,
            organization: formData.organization,
            preferredDate: formData.preferredDate,
            message: formData.message,
            intent: 'service_modal',
          },
        })

        isSubmitted.value = true
        setTimeout(() => {
          closeModal()
        }, 3000)
      } catch (error) {
        console.error('Error sending lead:', error)
        alert(
          `Opening WhatsApp failed. Please WhatsApp ${CONTACT_PHONE_DISPLAY} or email ${CONTACT_EMAIL}.`
        )
      } finally {
        isSubmitting.value = false
      }
    }

    return {
      formData,
      modalTitle,
      isSubmitting,
      isSubmitted,
      closeModal,
      handleSubmit
    }
  }
}
</script>
